/**
 * The tax-planning strategy library. Each strategy changes a year's inputs; a scenario is a set of
 * strategies (with their settings and the years they apply) projected over 1–5 years and compared
 * with the baseline. Every strategy says who it suits and what to check before recommending it.
 */
import { safeHarbor } from "../estimates";
import { Exact, roundHalfAwayFromZero } from "../finance/exact";
import { computeTaxWithMarginal, exactOf, FICA, ficaWagesOf, numOf, selfEmployment, wholeDollars, type TaxInputs, type TaxResult } from "./engine";
import { stateTax, type StateInputs, type StateResult } from "./state";
import { tableFor, type YearTable } from "./tables";

export type StrategyKey =
  | "roth_conversion"
  | "max_401k"
  | "hsa"
  | "retirement_plan_self_employed"
  | "s_corp_election"
  | "bunch_charity"
  | "qcd"
  | "harvest_losses"
  | "harvest_gains_zero"
  | "defer_income"
  | "ptet"
  | "augusta_rule";

export type StrategyParam = { key: string; label: string; kind: "dollars" | "percent" | "rate" | "count"; default: number; options?: number[] };

export type Strategy = {
  key: StrategyKey;
  title: string;
  summary: string;
  fits: string;
  checks: string;
  params: StrategyParam[];
  /** The client's action item when the plan is accepted. */
  actionItem: string;
  owner: "client" | "firm";
};

export const STRATEGIES: Strategy[] = [
  {
    key: "roth_conversion",
    title: "Roth conversion to the top of a bracket",
    summary: "Convert just enough of a traditional IRA each year to fill the chosen bracket, paying tax now at a known rate so later withdrawals are tax-free.",
    fits: "Retirees or low-income years before Social Security and required distributions start; people expecting higher rates later.",
    checks: "Medicare premium (IRMAA) brackets two years later, state tax, the five-year rule, and paying the tax from outside the IRA.",
    params: [
      { key: "fillToRate", label: "Fill up to the bracket", kind: "rate", default: 22, options: [12, 22, 24, 32] },
      { key: "maxPerYear", label: "Most to convert in a year", kind: "dollars", default: 100_000 },
    ],
    actionItem: "Convert the planned amount from your traditional IRA to your Roth IRA before December 31",
    owner: "client",
  },
  {
    key: "max_401k",
    title: "Maximize pre-tax 401(k) deferrals",
    summary: "Raise pre-tax deferrals to the year's limit, including catch-up contributions from age 50 (and the higher catch-up at ages 60–63).",
    fits: "Employees with room in their plan and cash flow to save more.",
    checks: "Whether the plan allows it, Roth vs. pre-tax, and that the employer match is captured.",
    params: [],
    actionItem: "Raise your 401(k) contribution to the yearly maximum with your employer",
    owner: "client",
  },
  {
    key: "hsa",
    title: "Fund a Health Savings Account",
    summary: "Contribute to an HSA up to the yearly limit: deductible now, tax-free for medical costs.",
    fits: "Anyone covered by a high-deductible health plan and not on Medicare.",
    checks: "HDHP coverage for the year, employer contributions count toward the limit.",
    params: [{ key: "amount", label: "Contribution", kind: "dollars", default: 4_400 }],
    actionItem: "Contribute the planned amount to your HSA",
    owner: "client",
  },
  {
    key: "retirement_plan_self_employed",
    title: "SEP-IRA or solo 401(k) for self-employment income",
    summary: "Contribute the employer share: 20% of net self-employment earnings (after half of SE tax), up to the yearly limit.",
    fits: "Sole proprietors and partners with self-employment income and no employees (or with a plan that covers them).",
    checks: "Employees who must be covered, plan setup deadlines (solo 401(k) by year-end for deferrals), cash flow.",
    params: [{ key: "pct", label: "Percent of the maximum", kind: "percent", default: 100 }],
    actionItem: "Open (or fund) a SEP-IRA or solo 401(k) and make the planned contribution",
    owner: "client",
  },
  {
    key: "s_corp_election",
    title: "S corporation election",
    summary: "Have the business taxed as an S corporation and pay the owner a reasonable salary; profit above the salary escapes self-employment tax.",
    fits: "Profitable sole proprietors (often above about $60,000–$80,000 of profit).",
    checks: "A reasonable salary, payroll costs, state fees and tax, the QBI effect, and the election deadline (Form 2553).",
    params: [{ key: "salary", label: "Owner's salary", kind: "dollars", default: 60_000 }],
    actionItem: "File Form 2553 to elect S corporation status and set up owner payroll",
    owner: "firm",
  },
  {
    key: "bunch_charity",
    title: "Bunch charitable gifts",
    summary: "Give two years of gifts in one year (directly or through a donor-advised fund) to itemize that year and take the standard deduction the next.",
    fits: "Regular givers whose itemized deductions are near the standard deduction.",
    checks: "From 2026 only gifts above 0.5% of AGI count when itemizing; non-itemizers get a smaller deduction for cash gifts.",
    params: [],
    actionItem: "Make two years of charitable gifts this year (for example through a donor-advised fund)",
    owner: "client",
  },
  {
    key: "qcd",
    title: "Qualified charitable distributions",
    summary: "Give to charity directly from an IRA after age 70½. The gift counts toward required distributions and is left out of income.",
    fits: "IRA owners 70½ or older who give to charity, especially non-itemizers.",
    checks: "The yearly QCD limit, gifts paid directly from the IRA to the charity, no donor-advised funds.",
    params: [{ key: "amount", label: "Gift from the IRA", kind: "dollars", default: 10_000 }],
    actionItem: "Ask your IRA custodian to send your charitable gifts directly to the charities",
    owner: "client",
  },
  {
    key: "harvest_losses",
    title: "Harvest investment losses",
    summary: "Sell investments with losses to offset gains, and up to $3,000 of other income; extra losses carry forward.",
    fits: "Taxable brokerage accounts with unrealized losses.",
    checks: "The wash-sale rule (no repurchase within 30 days) and the investment plan.",
    params: [{ key: "amount", label: "Losses to realize", kind: "dollars", default: 10_000 }],
    actionItem: "Review your brokerage account with your advisor to realize the planned losses",
    owner: "client",
  },
  {
    key: "harvest_gains_zero",
    title: "Harvest gains in the 0% bracket",
    summary: "Sell appreciated investments while taxable income stays within the 0% capital gains bracket, resetting cost basis tax-free.",
    fits: "Lower-income years with large unrealized gains.",
    checks: "State tax on the gains, effects on credits and Social Security taxation.",
    params: [{ key: "available", label: "Unrealized long-term gains available", kind: "dollars", default: 50_000 }],
    actionItem: "Sell and repurchase the planned investments to realize gains at the 0% rate",
    owner: "client",
  },
  {
    key: "defer_income",
    title: "Defer income to next year",
    summary: "Push business income (or a bonus) into next year, for example by timing invoices, when this year's rate is higher.",
    fits: "Cash-basis businesses and anyone with a higher rate this year than next.",
    checks: "Constructive receipt, cash flow, and next year's projected rate.",
    params: [{ key: "amount", label: "Income to defer", kind: "dollars", default: 20_000 }],
    actionItem: "Time year-end invoicing so the planned income is received in January",
    owner: "client",
  },
  {
    key: "ptet",
    title: "Pass-through entity tax election",
    summary: "Have the S corporation or partnership pay the owner's state income tax, deductible to the business, getting around the SALT cap.",
    fits: "Owners of pass-through businesses whose state taxes exceed the SALT cap (Maryland and Virginia offer an election; in DC a bill proposes one, so confirm whether it is in effect).",
    checks: "The state's election deadline and credit for the owner (Virginia Tax's 2025 instructions say its election no longer ends after 2026; confirm for the year), payment by December 31 for the deduction that year, cash flow at the entity, and the SALT cap phase-down.",
    params: [{ key: "stateRate", label: "State tax rate on the income", kind: "percent", default: 8 }],
    actionItem: "Elect the pass-through entity tax and have the business make the state payments",
    owner: "firm",
  },
  {
    key: "augusta_rule",
    title: "Rent your home to your business (up to 14 days)",
    summary: "The business rents the owner's home for meetings at a fair rate for up to 14 days; deductible to the business and tax-free to the owner (§280A(g)).",
    fits: "Business owners who hold genuine meetings at home.",
    checks: "A fair-market daily rate with evidence, meeting records, and an S corporation or partnership as the payer.",
    params: [
      { key: "days", label: "Days", kind: "count", default: 12 },
      { key: "dailyRate", label: "Daily rate", kind: "dollars", default: 800 },
    ],
    actionItem: "Document each business meeting at your home and have the business pay the agreed rent",
    owner: "client",
  },
];

export type ScenarioStrategy = { key: StrategyKey; params: Record<string, number>; years: number[] };
export type YearInputs = { federal: TaxInputs; state: StateInputs };
/**
 * A year's estimated payments, worked out the way estimates.ts `safeHarbor` does: in cents, the
 * required payment rounded up to the cent, installments rounded down to whole dollars with the
 * remainder on the last. `requiredAnnual` is in dollars (it can carry cents); `perQuarter` is the
 * first three installments in whole dollars.
 */
export type YearEstimates = { requiredAnnual: number; perQuarter: number; requiredAnnualCents: number; installmentsCents: [number, number, number, number] };
export type YearResult = {
  year: number;
  federal: TaxResult;
  state: StateResult;
  total: number;
  strategyNotes: string[];
  /** Federal (Form 1040-ES): on the 1040's tax, which leaves out an S corporation's payroll taxes. */
  estimates: YearEstimates;
  /** The state's (Maryland, Virginia or DC) from the state's own safe harbor; null when the plan has no state withholding to work from. */
  stateEstimates: YearEstimates | null;
};

/** Applies the scenario's strategies for one year; returns the changed inputs and notes. */
function applyStrategies(t: YearTable, inputs: YearInputs, strategies: ScenarioStrategy[], yearIndex: number, deferredIn: number, lastYear: boolean): { inputs: YearInputs; notes: string[]; deferredOut: number } {
  const f = { ...inputs.federal, otherIncome: inputs.federal.otherIncome };
  const s = { ...inputs.state };
  const notes: string[] = [];
  let deferredOut = 0;
  if (deferredIn) {
    f.businessIncome += deferredIn;
    notes.push(`Includes $${deferredIn.toLocaleString("en-US")} deferred from last year`);
  }
  const joint = f.filingStatus === "mfj";
  // An S corporation salary set by the election below, and whose part of the wages it is in.
  let salary = { who: "taxpayer" as "taxpayer" | "spouse", amount: 0 };
  for (const st of strategies) {
    if (!st.years.includes(yearIndex)) continue;
    const p = st.params;
    switch (st.key) {
      case "max_401k": {
        // Each person has their own limit, by their own age and wages (on a joint plan the spouse's
        // part of the wages is theirs; with no split recorded all wages are the taxpayer's).
        const people: Array<{ who: "taxpayer" | "spouse"; age: number; wages: number; deferrals: number }> = joint
          ? [
              { who: "taxpayer", age: f.ages[0] ?? 45, wages: f.wages - (f.spouseWages ?? 0), deferrals: f.deferrals - (f.spouseDeferrals ?? 0) },
              { who: "spouse", age: f.ages[1] ?? f.ages[0] ?? 45, wages: f.spouseWages ?? 0, deferrals: f.spouseDeferrals ?? 0 },
            ]
          : [{ who: "taxpayer", age: f.ages[0] ?? 45, wages: f.wages, deferrals: f.deferrals }];
        for (const pp of people) {
          // The spouse defers from their own wages; an S corporation salary paid to the spouse isn't deferred here (the
          // corporation's plan isn't modelled, as for the SEP), which keeps the one limit the couple had before.
          if (pp.who === "spouse" && salary.who === "spouse") pp.wages -= salary.amount;
          if (pp.who === "spouse" && pp.wages <= 0) continue;
          // From 2026, catch-up contributions must be Roth for those with over $150,000 of FICA wages
          // the year before (SECURE 2.0 §603); this year's wages stand in for last year's.
          const catchUp = pp.age >= 60 && pp.age <= 63 ? t.limits.k401SuperCatchUp : pp.age >= 50 ? t.limits.k401CatchUp : 0;
          const rothCatchUp = t.year >= 2026 && catchUp > 0 && pp.wages + pp.deferrals > 150_000;
          const limit = t.limits.k401 + (rothCatchUp ? 0 : catchUp);
          const add = Math.max(0, Math.min(limit - pp.deferrals, pp.wages));
          f.deferrals += add;
          f.wages -= add;
          if (pp.who === "spouse") {
            f.spouseDeferrals = (f.spouseDeferrals ?? 0) + add;
            f.spouseWages = (f.spouseWages ?? 0) - add;
          }
          const who = pp.who === "spouse" ? "The spouse defers" : "Defers";
          if (add) notes.push(`${who} $${add.toLocaleString("en-US")} more into the 401(k) (pre-tax limit $${limit.toLocaleString("en-US")})`);
          if (rothCatchUp) notes.push(`The $${catchUp.toLocaleString("en-US")} catch-up must go in as Roth (no deduction) because ${pp.who === "spouse" ? "the spouse's " : ""}wages are over $150,000`);
        }
        break;
      }
      case "hsa": {
        const amt = Math.max(0, p.amount ?? 0);
        f.hsaDeduction += amt;
        notes.push(`HSA contribution of $${amt.toLocaleString("en-US")}`);
        break;
      }
      case "retirement_plan_self_employed": {
        const seIncome = (f.businessIsSCorp ? 0 : f.businessIncome) + f.partnershipSeIncome;
        if (seIncome <= 0) {
          if (f.businessIsSCorp) notes.push("As an S corporation the plan is funded from the owner's salary (up to 25% of it); not modeled here");
          break;
        }
        // Half of self-employment tax, each spouse's on their own wage base (engine.ts selfEmployment).
        const halfSe = selfEmployment(t, f).halfSe;
        // The employer share (SEP or solo 401(k) profit sharing), within the year's limit with what's already in.
        const room = exactOf(t.limits.sepMax - f.sepContribution);
        const twenty = exactOf(seIncome).sub(halfSe).mul(Exact.decimal("0.2"));
        const capped = twenty.cmp(room) <= 0 ? twenty : room;
        const max = capped.sign() < 0 ? Exact.int(0) : capped;
        const amt = wholeDollars(max.mul(exactOf(Math.min(100, p.pct ?? 100))).div(Exact.int(100)));
        f.sepContribution += amt;
        notes.push(`Employer retirement contribution of $${amt.toLocaleString("en-US")} (20% of net self-employment earnings)`);
        break;
      }
      case "s_corp_election": {
        if (f.businessIncome <= 0 || f.businessIsSCorp) break;
        // The salary is wages, with Social Security (to the wage base) and Medicare on both sides;
        // the employer half is a business expense. The business keeps its own wages and property.
        // Social Security stops at the wage base less the owner's own other wages (per person on a joint plan).
        const pay = Math.min(p.salary ?? 0, f.businessIncome);
        const ssRoom = Math.max(0, t.ssWageBase - ficaWagesOf(f, f.businessOwner ?? null));
        const halfX = FICA.socialSecurity.mul(exactOf(Math.min(pay, ssRoom))).add(FICA.medicare.mul(exactOf(pay)));
        const half = numOf(halfX);
        f.wages += pay;
        if (joint && f.businessOwner === "spouse") f.spouseWages = (f.spouseWages ?? 0) + pay;
        salary = { who: joint && f.businessOwner === "spouse" ? "spouse" : "taxpayer", amount: pay };
        f.businessW2Wages += pay;
        f.businessIncome = f.businessIncome - pay - half;
        f.businessIsSCorp = true;
        f.payrollTaxes += numOf(halfX.mul(Exact.int(2)));
        notes.push(`Owner salary $${pay.toLocaleString("en-US")} with $${wholeDollars(halfX.mul(Exact.int(2))).toLocaleString("en-US")} of payroll tax (both halves); the rest is S corporation profit`);
        break;
      }
      case "bunch_charity": {
        const give = f.charityCash;
        f.charityCash = yearIndex % 2 === 0 ? give * 2 : 0;
        notes.push(yearIndex % 2 === 0 ? "Two years of gifts this year" : "No gifts this year (given last year)");
        break;
      }
      case "qcd": {
        // 70½ by the gift: someone 71 at year-end qualifies all year; each spouse has their own limit.
        const eligible = (f.filingStatus === "mfj" ? f.ages.slice(0, 2) : f.ages.slice(0, 1)).filter((a) => a >= 71).length;
        if (!eligible) {
          notes.push("No one is 70½ for the whole year yet, so no qualified charitable distributions are modeled");
          break;
        }
        const amt = Math.min(p.amount ?? 0, f.retirementDistributions, t.limits.qcd * eligible);
        f.retirementDistributions -= amt;
        f.charityCash = Math.max(0, f.charityCash - amt);
        notes.push(`$${amt.toLocaleString("en-US")} given directly from the IRA`);
        break;
      }
      case "harvest_losses": {
        const amt = Math.max(0, p.amount ?? 0);
        f.shortTermGains -= amt;
        notes.push(`Realizes $${amt.toLocaleString("en-US")} of losses`);
        break;
      }
      case "defer_income": {
        if (lastYear) {
          notes.push("Not deferred in the plan's last year: the income would be taxed in a year the plan doesn't show");
          break;
        }
        const amt = Math.min(Math.max(0, p.amount ?? 0), Math.max(0, f.businessIncome));
        f.businessIncome -= amt;
        deferredOut = amt;
        notes.push(`Defers $${amt.toLocaleString("en-US")} of business income to next year`);
        break;
      }
      case "ptet": {
        if (s.code === "DC") {
          notes.push("Not modeled for DC: confirm whether DC has a pass-through entity tax election in effect for the year");
          break;
        }
        const sc = f.businessIsSCorp ? Math.max(0, f.businessIncome) : 0;
        const k1 = Math.max(0, f.sCorpIncome);
        const pship = Math.max(0, f.partnershipSeIncome);
        const base = sc + k1 + pship;
        if (base <= 0) break;
        const tax = wholeDollars(exactOf(base).mul(exactOf(p.stateRate ?? 0)).div(Exact.int(100)));
        // Deducted by the business, in proportion to each entity's income.
        if (sc) f.businessIncome -= (tax * sc) / base;
        if (k1) f.sCorpIncome -= (tax * k1) / base;
        if (pship) f.partnershipSeIncome -= (tax * pship) / base;
        // The owner no longer pays that state tax personally; the state adds it back and credits it.
        f.stateLocalTaxes = Math.max(0, f.stateLocalTaxes - tax);
        s.ptetAddBack = (s.ptetAddBack ?? 0) + tax;
        notes.push(`Business pays $${tax.toLocaleString("en-US")} of state tax for the owner (deductible federally; the state credits it)`);
        break;
      }
      case "augusta_rule": {
        // Only a separate entity (S corporation or partnership) can rent the owner's home (§280A(g)).
        const rent = Math.min(14, Math.max(0, p.days ?? 0)) * Math.max(0, p.dailyRate ?? 0);
        if (f.businessIsSCorp && f.businessIncome > 0) f.businessIncome -= Math.min(rent, f.businessIncome);
        else if (f.sCorpIncome > 0) f.sCorpIncome -= Math.min(rent, f.sCorpIncome);
        else if (f.partnershipSeIncome > 0) f.partnershipSeIncome -= Math.min(rent, f.partnershipSeIncome);
        else {
          notes.push("Needs an S corporation or partnership to pay the rent; a sole proprietor can't rent to themselves");
          break;
        }
        notes.push(`Business pays $${rent.toLocaleString("en-US")} of rent for meetings at home (tax-free to the owner)`);
        break;
      }
      default:
        break;
    }
  }
  return { inputs: { federal: f, state: s }, notes, deferredOut };
}

/** The most that can be added as ordinary income while staying at or below `limit` of ordinary taxable income. */
function fillTo(t: YearTable, x: TaxInputs, limit: number, cap: number, add: (x: TaxInputs, a: number) => TaxInputs, measure: (r: TaxResult) => number): number {
  if (measure(computeTaxWithMarginal(t, x)) >= limit) return 0;
  let lo = 0;
  let hi = cap;
  if (measure(computeTaxWithMarginal(t, add(x, hi))) <= limit) return hi;
  for (let i = 0; i < 40 && hi - lo > 1; i++) {
    const mid = (lo + hi) / 2;
    if (measure(computeTaxWithMarginal(t, add(x, mid))) <= limit) lo = mid;
    else hi = mid;
  }
  return Math.floor(lo);
}

/** Grows a year's inputs by the plan's assumptions. */
export function growInputs(base: YearInputs, years: number, incomeGrowthPct: number): YearInputs {
  // (1 + growth)^years exactly, each amount rounded once, half away from zero.
  let g = Exact.int(1);
  const step = Exact.int(1).add(exactOf(incomeGrowthPct).div(Exact.int(100)));
  for (let i = 0; i < years; i++) g = g.mul(step);
  const f = base.federal;
  const grow = (v: number) => wholeDollars(exactOf(v).mul(g));
  return {
    federal: {
      ...f,
      ages: f.ages.map((a) => a + years),
      wages: grow(f.wages),
      deferrals: f.deferrals,
      spouseWages: grow(f.spouseWages ?? 0),
      businessIncome: grow(f.businessIncome),
      sCorpIncome: grow(f.sCorpIncome),
      partnershipSeIncome: grow(f.partnershipSeIncome),
      businessW2Wages: grow(f.businessW2Wages),
      passThroughW2Wages: grow(f.passThroughW2Wages),
      // Investment, retirement and one-off items stay as entered; staff change them year by year if needed.
      withholding: grow(f.withholding),
    },
    state: base.state.withholding == null ? base.state : { ...base.state, withholding: grow(base.state.withholding) },
  };
}

const cents = (dollars: number) => roundHalfAwayFromZero(dollars * 100);
/** A year's estimates from a safe harbor in cents (see YearEstimates). */
function estimatesOf(code: string, x: { priorTax: number | null; priorAgi: number | null; mfs: boolean; tax: number; withholding: number }): YearEstimates {
  const sh = safeHarbor(code, {
    priorYearTaxCents: x.priorTax === null ? null : cents(Math.max(0, x.priorTax)),
    priorYearAgiCents: x.priorAgi === null ? null : cents(x.priorAgi),
    marriedFilingSeparately: x.mfs,
    // A year whose credits exceed its tax requires no estimates.
    projectedTaxCents: cents(Math.max(0, x.tax)),
    withholdingCents: cents(Math.max(0, x.withholding)),
  })!;
  return { requiredAnnual: sh.requiredAnnualCents / 100, perQuarter: sh.installments[0] / 100, requiredAnnualCents: sh.requiredAnnualCents, installmentsCents: sh.installments };
}

export type ProjectionInput = {
  baseYear: number;
  years: number;
  incomeGrowthPct: number;
  inflationPct: number;
  base: YearInputs;
  /** Last year's total federal tax and AGI, for the first year's estimate safe harbor. */
  priorYearTax: number | null;
  priorYearAgi: number | null;
};

/**
 * Projects a scenario year by year (strategies apply in the years listed, 0 = the base year).
 * `overrides` replaces federal inputs in a calendar year after growth and before the strategies,
 * for example business income taken from a CFO forecast.
 */
export function project(input: ProjectionInput, strategies: ScenarioStrategy[], overrides: Record<number, Partial<TaxInputs>> = {}): YearResult[] {
  const out: YearResult[] = [];
  let carry = 0;
  let priorTax = input.priorYearTax;
  let priorAgi = input.priorYearAgi;
  let priorStateTax: number | null = null;
  let lossCarry = input.base.federal.capitalLossCarryover;
  let ltLossCarry = input.base.federal.longTermLossCarryover ?? 0;
  for (let i = 0; i < input.years; i++) {
    const year = input.baseYear + i;
    const t = tableFor(year, input.inflationPct);
    const grown = growInputs(input.base, i, input.incomeGrowthPct);
    if (overrides[year]) grown.federal = { ...grown.federal, ...overrides[year] };
    grown.federal.capitalLossCarryover = lossCarry;
    grown.federal.longTermLossCarryover = ltLossCarry;
    const applied = applyStrategies(t, grown, strategies, i, carry, i === input.years - 1);
    let f = applied.inputs.federal;
    const notes = applied.notes;
    carry = applied.deferredOut;

    const roth = strategies.find((s) => s.key === "roth_conversion" && s.years.includes(i));
    if (roth) {
      const rate = roth.params.fillToRate ?? 22;
      const idx = [10, 12, 22, 24, 32, 35].indexOf(rate);
      const top = idx >= 0 ? t.brackets[f.filingStatus][idx]! : null;
      if (top) {
        const amt = fillTo(t, f, top, Math.max(0, roth.params.maxPerYear ?? 0), (x, a) => ({ ...x, rothConversion: x.rothConversion + a }), (r) => r.ordinaryTaxableIncome);
        f = { ...f, rothConversion: f.rothConversion + amt };
        notes.push(amt > 0 ? `Converts $${amt.toLocaleString("en-US")} to Roth (fills the ${rate}% bracket)` : `No room left in the ${rate}% bracket`);
      }
    }
    const gains = strategies.find((s) => s.key === "harvest_gains_zero" && s.years.includes(i));
    if (gains) {
      const zeroTop = t.capGains[f.filingStatus][0];
      const amt = fillTo(t, f, zeroTop, Math.max(0, gains.params.available ?? 0), (x, a) => ({ ...x, longTermGains: x.longTermGains + a }), (r) => r.taxableIncome);
      f = { ...f, longTermGains: f.longTermGains + amt };
      notes.push(`Realizes $${amt.toLocaleString("en-US")} of long-term gains at 0%`);
    }

    const federal = computeTaxWithMarginal(t, f);
    const state = stateTax(year, input.inflationPct, applied.inputs.state, f, federal);
    // Next year's carryovers: losses beyond the $3,000 (or $1,500) limit, short-term used first.
    const stNet = f.shortTermGains - f.capitalLossCarryover;
    const ltNet = f.longTermGains - (f.longTermLossCarryover ?? 0);
    const limit = f.filingStatus === "mfs" ? 1_500 : 3_000;
    if (stNet + ltNet >= 0) {
      lossCarry = 0;
      ltLossCarry = 0;
    } else {
      // Net each side against the other's gain, then the deduction absorbs short-term loss first.
      let stL = stNet < 0 ? -stNet - Math.max(0, ltNet) : 0;
      let ltL = ltNet < 0 ? -ltNet - Math.max(0, stNet) : 0;
      stL = Math.max(0, stL);
      ltL = Math.max(0, ltL);
      const fromSt = Math.min(stL, limit);
      lossCarry = stL - fromSt;
      ltLossCarry = Math.max(0, ltL - (limit - fromSt));
    }
    // Estimates for this year: the lesser of 90% of this year's tax and 100% (110% over $150,000 AGI) of
    // last year's (IRC §6654(d)). The tax is the 1040's: an S corporation owner's Social Security and
    // Medicare are the corporation's payroll taxes (Form 941), withheld from the salary, never estimates.
    const forEstimates = federal.totalTax - federal.payrollTaxes;
    const estimates = estimatesOf("federal", { priorTax, priorAgi, mfs: f.filingStatus === "mfs", tax: forEstimates, withholding: f.withholding });
    const st = applied.inputs.state;
    const stateEstimates = (st.code === "MD" || st.code === "VA" || st.code === "DC") && st.withholding != null ? estimatesOf(st.code, { priorTax: priorStateTax, priorAgi: null, mfs: false, tax: state.totalTax, withholding: st.withholding }) : null;
    out.push({ year, federal, state, total: federal.totalTax + state.totalTax, strategyNotes: notes, estimates, stateEstimates });
    priorTax = forEstimates;
    priorAgi = federal.agi;
    priorStateTax = state.totalTax;
  }
  return out;
}
